import PropTypes from 'prop-types'
import React, { useRef, useEffect } from 'react'

const AutoScrollContainer = ({
  children,
  mode = 'step', // 'step' or 'smooth'
  interval = 5000,
  cardHeight = 115,
  gap = 16,
  className = '',
  style = {},
  scrollSpeed = 1, // 1 = 60fps, 0.5 = 30fps, 0.25 = 15fps
  length = 0,
}) => {
  const containerRef = useRef(null)

  // Step scrolling effect
  useEffect(() => {
    const el = containerRef.current
    if (!el || mode === 'smooth') return

    let isPaused = false

    const scroll = () => {
      if (!el || isPaused) return

      const maxScroll = el.scrollHeight - el.clientHeight
      const next = el.scrollTop + cardHeight + gap

      if (next >= maxScroll) {
        // instead of looping instantly, scroll back to top smoothly
        el.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        el.scrollTo({ top: next, behavior: 'smooth' })
      }
    }

    const timer = setInterval(scroll, interval)

    const pause = () => (isPaused = true)
    const resume = () => (isPaused = false)

    el.addEventListener('mouseenter', pause)
    el.addEventListener('mouseleave', resume)

    return () => {
      clearInterval(timer)
      el.removeEventListener('mouseenter', pause)
      el.removeEventListener('mouseleave', resume)
    }
  }, [mode, interval, cardHeight, gap])

  // Smooth scrolling effect
  useEffect(() => {
    if (mode !== 'smooth') return

    const el = containerRef.current
    if (!el) return

    const content = el.firstChild
    if (!content) return

    let frame
    let offset = 0
    let isPaused = false
    let goingBack = false
    let pauseTimer

    const scroll = () => {
      if (isPaused) return

      const maxScroll = el.scrollHeight - el.clientHeight

      if (!goingBack) {
        // going down
        offset += scrollSpeed
        if (offset >= maxScroll) {
          offset = maxScroll
          cancelAnimationFrame(frame)

          // pause at bottom
          pauseTimer = setTimeout(() => {
            goingBack = true
            frame = requestAnimationFrame(scroll)
          }, 1000) // 1s pause
          return
        }
      } else {
        // going back up quickly
        offset -= scrollSpeed * 10
        if (offset <= 0) {
          offset = 0
          cancelAnimationFrame(frame)

          // pause at top
          pauseTimer = setTimeout(() => {
            goingBack = false
            frame = requestAnimationFrame(scroll)
          }, 1000) // 1s pause
          return
        }
      }

      el.scrollTop = offset
      frame = requestAnimationFrame(scroll)
    }

    const pause = () => {
      isPaused = true
      cancelAnimationFrame(frame)
      clearTimeout(pauseTimer)
    }

    const resume = () => {
      if (!isPaused) return
      isPaused = false
      frame = requestAnimationFrame(scroll)
    }

    frame = requestAnimationFrame(scroll)

    el.addEventListener('mouseenter', pause)
    el.addEventListener('mouseleave', resume)

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(pauseTimer)
      el.removeEventListener('mouseenter', pause)
      el.removeEventListener('mouseleave', resume)
    }
  }, [mode, scrollSpeed, length])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: gap,
        height: `calc((${cardHeight}px * 5) + (${gap}px * 5))`,
        position: 'relative',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        ...style,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: gap }}>
        {children}
      </div>
    </div>
  )
}

AutoScrollContainer.propTypes = {
  gap: PropTypes.number,
  mode: PropTypes.oneOf(['step', 'smooth']),
  style: PropTypes.object,
  length: PropTypes.number,
  children: PropTypes.node.isRequired,
  interval: PropTypes.number,
  className: PropTypes.string,
  cardHeight: PropTypes.number,
  scrollSpeed: PropTypes.number,
}

export default AutoScrollContainer
